import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  ArrowLeft, ArrowRight, Check, Image, Target, DollarSign,
  Rocket, Loader2, Megaphone, AlertCircle, Link2
} from 'lucide-react';
import AppLayout from '../../layouts/AppLayout';
import LocationSelector from '../MPFlow/components/targeting/LocationSelector';
import AgeRangeSlider from '../MPFlow/components/targeting/AgeRangeSlider';
import GenderSelector from '../MPFlow/components/targeting/GenderSelector';
import InterestTagsInput from '../MPFlow/components/targeting/InterestTagsInput';
import BudgetInput from '../MPFlow/components/targeting/BudgetInput';
import { metaAdsService, googleAdsService } from '../../services';
import '../MPFlow/mp.css';
import './styles.css';

/* ── Platform selector config ────────── */
const PLATFORMS = [
  {
    id: 'meta',
    name: 'Meta Ads',
    emoji: '📘',
    desc: 'Facebook · Instagram',
    gradient: 'linear-gradient(135deg, #1877F2, #0C63D4)',
  },
  {
    id: 'google',
    name: 'Google Ads',
    emoji: '🔍',
    desc: 'Pesquisa · YouTube · Display',
    gradient: 'linear-gradient(135deg, #4285F4, #0F9D58)',
  },
];

const META_OBJECTIVES = [
  { value: 'OUTCOME_TRAFFIC',      label: 'Tráfego',       desc: 'Enviar pessoas para um site' },
  { value: 'OUTCOME_ENGAGEMENT',   label: 'Engajamento',   desc: 'Curtidas, comentários e compartilhamentos' },
  { value: 'OUTCOME_LEADS',        label: 'Leads',          desc: 'Coletar informações de contato' },
  { value: 'OUTCOME_AWARENESS',    label: 'Reconhecimento', desc: 'Alcançar o máximo de pessoas' },
];

const GOOGLE_OBJECTIVES = [
  { value: 'demand_gen',       label: 'Demand Gen',       desc: 'YouTube, Gmail e Discover' },
  { value: 'performance_max',  label: 'Performance Max',  desc: 'IA do Google em todos os canais' },
];

function AdsCreatePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [platform, setPlatform] = useState(searchParams.get('platform') || '');
  const [step, setStep] = useState(0);
  const [launching, setLaunching] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  /* ── connection check ──────────────── */
  const [metaConnected, setMetaConnected] = useState(null);
  const [googleConnected, setGoogleConnected] = useState(null);

  useEffect(() => {
    Promise.allSettled([
      metaAdsService.getStatus(),
      googleAdsService.getStatus(),
    ]).then(([meta, google]) => {
      setMetaConnected(!!meta.value?.connected);
      setGoogleConnected(!!google.value?.connected);
    });
  }, []);

  const isConnected = (id) => (id === 'meta' ? metaConnected : googleConnected);

  const objectives = platform === 'google' ? GOOGLE_OBJECTIVES : META_OBJECTIVES;

  /* ── creative step state ───────────── */
  const [creative, setCreative] = useState({
    name: '',
    objective: '',
    headline: '',
    message: '',
    destination_url: '',
    image_url: '',
  });

  /* ── targeting step state ──────────── */
  const [targeting, setTargeting] = useState({
    location: '',
    location_key: '',
    location_type: 'city',
    age_min: 18,
    age_max: 55,
    gender: 0,
    interests: [],
  });

  /* ── budget step state ─────────────── */
  const [budget, setBudget] = useState(50);

  /* ── When platform changes, reset objective ── */
  useEffect(() => {
    if (platform) {
      setCreative(p => ({
        ...p,
        objective: platform === 'google' ? 'demand_gen' : 'OUTCOME_TRAFFIC'
      }));
    }
  }, [platform]);

  /* ── Steps config ──────────────────── */
  const STEPS = [
    { key: 'creative',  label: 'Criativo',     icon: <Image size={18} /> },
    { key: 'targeting',  label: 'Segmentação',  icon: <Target size={18} /> },
    { key: 'budget',     label: 'Orçamento',    icon: <DollarSign size={18} /> },
    { key: 'review',     label: 'Publicar',     icon: <Rocket size={18} /> },
  ];

  const canNext = () => {
    if (step === 0) return creative.name && creative.headline && creative.message;
    if (step === 1) return platform === 'google' || targeting.location;
    if (step === 2) return budget >= 5;
    return true;
  };

  const handleLaunch = async () => {
    setError('');
    setLaunching(true);
    try {
      let data;
      if (platform === 'meta') {
        const payload = {
          ...creative,
          daily_budget: budget * 100,
          target_age_min: targeting.age_min,
          target_age_max: targeting.age_max,
          target_gender: targeting.gender,
          target_country: 'BR',
          target_location_key: targeting.location_key,
          target_location_type: targeting.location_type,
          target_interests: targeting.interests,
        };
        data = await metaAdsService.launchCampaign(payload);
      } else {
        const payload = {
          name: creative.name,
          type: creative.objective, // demand_gen | performance_max
          headline: creative.headline,
          description: creative.message,
          final_url: creative.destination_url,
          image_url: creative.image_url,
          daily_budget: budget * 100,
        };
        data = await googleAdsService.createCampaign(payload);
      }
      if (data?.success) {
        setResult(data);
      } else {
        setError(data?.message || data?.meta_error || data?.error || 'Falha ao criar campanha');
      }
    } catch {
      setError('Erro de conexão ao criar campanha');
    } finally {
      setLaunching(false);
    }
  };

  /* ── RENDER ────────────────────────── */

  /* Step -1: Platform selection */
  if (!platform) {
    return (
      <AppLayout>
        <div className="ads-create">
          <header className="ads-create__header">
            <button className="ads-create__back" onClick={() => navigate('/ads-manager')}>
              <ArrowLeft size={18} /> Voltar
            </button>
            <h1 className="ads-create__title"><Megaphone size={22} /> Criar Nova Campanha</h1>
          </header>

          <div className="ads-create__card">
            <h2 className="ads-create__section-title"><Rocket size={20} /> Onde você quer anunciar?</h2>
            <p className="ads-create__plat-hint">Escolha a plataforma para criar seu anúncio</p>

            <div className="ads-create__platforms">
              {PLATFORMS.map(p => {
                const connected = isConnected(p.id);
                return (
                  <button
                    key={p.id}
                    className={`ads-create__plat-card ${connected === false ? 'disabled' : ''}`}
                    onClick={() => connected ? setPlatform(p.id) : navigate('/ads/connect')}
                  >
                    <div className="ads-create__plat-top" style={{ background: p.gradient }}>
                      <span className="ads-create__plat-emoji">{p.emoji}</span>
                      <strong>{p.name}</strong>
                    </div>
                    <div className="ads-create__plat-body">
                      <span className="ads-create__plat-desc">{p.desc}</span>
                      {connected === null ? (
                        <span className="ads-create__plat-status loading"><Loader2 size={14} className="spin" /> Verificando...</span>
                      ) : connected ? (
                        <span className="ads-create__plat-status connected">✓ Conectado</span>
                      ) : (
                        <span className="ads-create__plat-status disconnected">
                          <Link2 size={14} /> Conectar conta
                        </span>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="ads-create">
        {/* Header */}
        <header className="ads-create__header">
          <button className="ads-create__back" onClick={() => {
            if (step > 0) setStep(s => s - 1);
            else setPlatform('');
          }}>
            <ArrowLeft size={18} /> {step > 0 ? 'Voltar' : 'Trocar Plataforma'}
          </button>
          <h1 className="ads-create__title">
            <Megaphone size={22} />
            {platform === 'meta' ? '📘 Campanha Meta Ads' : '🔍 Campanha Google Ads'}
          </h1>
        </header>

        {/* Stepper */}
        <div className="ads-create__stepper">
          {STEPS.map((s, i) => (
            <div key={s.key} className={`ads-create__step ${i < step ? 'done' : ''} ${i === step ? 'active' : ''}`}>
              <div className="ads-create__step-dot">
                {i < step ? <Check size={14} /> : s.icon}
              </div>
              <span className="ads-create__step-label">{s.label}</span>
            </div>
          ))}
        </div>

        {/* Success */}
        {result ? (
          <div className="ads-create__card ads-create__success">
            <Check size={48} />
            <h2>Campanha Criada!</h2>
            <p>Sua campanha foi enviada para o {platform === 'meta' ? 'Meta Ads' : 'Google Ads'} com sucesso.</p>
            {(result.campaign_id || result.id) && (
              <p className="ads-create__camp-id">ID: {result.campaign_id || result.id}</p>
            )}
            <button className="ads-mgr__btn-create" onClick={() => navigate('/ads-manager')}>
              Ir para Gerenciador
            </button>
          </div>
        ) : (
          <div className="ads-create__card">
            {/* Step 0 — Creative */}
            {step === 0 && (
              <div className="ads-create__section">
                <h2 className="ads-create__section-title"><Image size={20} /> Informações do Anúncio</h2>

                <label className="ads-create__label">Nome da Campanha *</label>
                <input className="mp-field__input" value={creative.name}
                  onChange={e => setCreative(p => ({ ...p, name: e.target.value }))}
                  placeholder="Ex: Black Friday 2025" />

                <label className="ads-create__label">Objetivo</label>
                <div className="ads-create__objectives">
                  {objectives.map(o => (
                    <button key={o.value}
                      className={`ads-create__obj-btn ${creative.objective === o.value ? 'active' : ''}`}
                      onClick={() => setCreative(p => ({ ...p, objective: o.value }))}>
                      <strong>{o.label}</strong>
                      <span>{o.desc}</span>
                    </button>
                  ))}
                </div>

                <label className="ads-create__label">Título do Anúncio *</label>
                <input className="mp-field__input" value={creative.headline}
                  onChange={e => setCreative(p => ({ ...p, headline: e.target.value }))}
                  placeholder="Chamada principal do anúncio" />

                <label className="ads-create__label">Texto (Corpo) *</label>
                <textarea className="mp-field__input" rows={3} value={creative.message}
                  onChange={e => setCreative(p => ({ ...p, message: e.target.value }))}
                  placeholder="Texto que acompanha o anúncio" />

                <label className="ads-create__label">URL de Destino</label>
                <input className="mp-field__input" type="url" value={creative.destination_url}
                  onChange={e => setCreative(p => ({ ...p, destination_url: e.target.value }))}
                  placeholder="https://seusite.com/oferta" />

                <label className="ads-create__label">URL da Imagem</label>
                <input className="mp-field__input" type="url" value={creative.image_url}
                  onChange={e => setCreative(p => ({ ...p, image_url: e.target.value }))}
                  placeholder="https://... ou deixe vazio para usar criativo gerado" />
              </div>
            )}

            {/* Step 1 — Targeting */}
            {step === 1 && (
              <div className="ads-create__section">
                <h2 className="ads-create__section-title"><Target size={20} /> Segmentação do Público</h2>

                {platform === 'google' ? (
                  <div className="ads-create__google-targeting">
                    <p className="ads-create__google-hint">
                      O Google Ads usa segmentação automática com IA.
                      A segmentação será otimizada pelo algoritmo do Google
                      com base no objetivo da campanha.
                    </p>
                    <label className="ads-create__label">Localização (opcional)</label>
                    <div className="seg-step-input">
                      <LocationSelector
                        value={targeting.location}
                        onChange={(loc) => setTargeting(p => ({
                          ...p,
                          location: loc.name,
                          location_key: loc.key,
                          location_type: loc.type
                        }))}
                      />
                    </div>
                  </div>
                ) : (
                  <>
                    <label className="ads-create__label">Localização</label>
                    <div className="seg-step-input">
                      <LocationSelector
                        value={targeting.location}
                        onChange={(loc) => setTargeting(p => ({
                          ...p,
                          location: loc.name,
                          location_key: loc.key,
                          location_type: loc.type
                        }))}
                      />
                    </div>

                    <label className="ads-create__label">Faixa Etária</label>
                    <div className="seg-step-input">
                      <AgeRangeSlider
                        min={targeting.age_min}
                        max={targeting.age_max}
                        onChange={(min, max) => setTargeting(p => ({ ...p, age_min: min, age_max: max }))}
                      />
                    </div>

                    <label className="ads-create__label">Gênero</label>
                    <div className="seg-step-input">
                      <GenderSelector
                        value={targeting.gender}
                        onChange={(g) => setTargeting(p => ({ ...p, gender: g }))}
                      />
                    </div>

                    <label className="ads-create__label">Interesses</label>
                    <div className="seg-step-input">
                      <InterestTagsInput
                        value={targeting.interests}
                        onChange={(tags) => setTargeting(p => ({ ...p, interests: tags }))}
                      />
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Step 2 — Budget */}
            {step === 2 && (
              <div className="ads-create__section">
                <h2 className="ads-create__section-title"><DollarSign size={20} /> Orçamento Diário</h2>
                <div className="seg-step-input">
                  <BudgetInput value={budget} onChange={setBudget} />
                </div>
              </div>
            )}

            {/* Step 3 — Review */}
            {step === 3 && (
              <div className="ads-create__section">
                <h2 className="ads-create__section-title"><Rocket size={20} /> Resumo da Campanha</h2>

                <div className="ads-create__review">
                  <ReviewRow label="Plataforma" value={platform === 'meta' ? '📘 Meta Ads' : '🔍 Google Ads'} />
                  <ReviewRow label="Campanha" value={creative.name} />
                  <ReviewRow label="Objetivo" value={objectives.find(o => o.value === creative.objective)?.label} />
                  <ReviewRow label="Título" value={creative.headline} />
                  <ReviewRow label="Texto" value={creative.message} />
                  {targeting.location && <ReviewRow label="Local" value={targeting.location} />}
                  {platform === 'meta' && (
                    <>
                      <ReviewRow label="Idade" value={`${targeting.age_min} — ${targeting.age_max} anos`} />
                      <ReviewRow label="Gênero" value={targeting.gender === 0 ? 'Todos' : targeting.gender === 1 ? 'Homens' : 'Mulheres'} />
                      <ReviewRow label="Interesses" value={targeting.interests.length ? targeting.interests.join(', ') : 'Nenhum'} />
                    </>
                  )}
                  <ReviewRow label="Orçamento" value={`R$ ${budget},00/dia`} accent />
                </div>

                {error && (
                  <div className="ads-create__error">
                    <AlertCircle size={16} /> {error}
                  </div>
                )}
              </div>
            )}

            {/* NAV */}
            <div className="ads-create__nav">
              {step > 0 && (
                <button className="ads-create__btn-back" onClick={() => setStep(s => s - 1)}>
                  <ArrowLeft size={16} /> Voltar
                </button>
              )}
              {step < 3 ? (
                <button className="ads-mgr__btn-create" disabled={!canNext()} onClick={() => setStep(s => s + 1)}>
                  Próximo <ArrowRight size={16} />
                </button>
              ) : (
                <button className="ads-mgr__btn-create" onClick={handleLaunch} disabled={launching}>
                  {launching ? <><Loader2 size={16} className="spin" /> Publicando...</> : <><Rocket size={16} /> Publicar Campanha</>}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}

function ReviewRow({ label, value, accent }) {
  return (
    <div className="ads-create__review-row">
      <span className="ads-create__review-label">{label}</span>
      <span className={`ads-create__review-value ${accent ? 'accent' : ''}`}>{value}</span>
    </div>
  );
}

export default AdsCreatePage;
